import { useState, useRef, useCallback } from 'react';
import { captureError } from '@/utils/captureError';
import { compressImageForStorage, compressImageForThumbnail } from '@/utils/imageCompression';
import { findDuplicates } from '@/services/duplicateService';
import { inventoryService } from '@/services/inventoryService';
import { uploadLabelImage, deleteLabelImage } from '@/services/storageService';
import { enrichWine } from '@/services/enrichmentService';
import { sanitizeWineName } from '@/utils/wineNameGuard';
import { showToast } from '@/components/rc';
import { CONFIG } from '@/constants';
import type { ScanSession } from '@/hooks/useScanSession';
import { confirmProdWrite } from '@/components/ProdWriteGuard';
import { hapticHeavy } from '@/utils/haptics';
import { trackEvent } from '@/config/analytics';
import { openFilePicker } from '@/components/scan/ModeSelector';
import type { Wine, WineDraft, DuplicateCandidate, CommitStage } from '@/types';
import type { Currency } from '@/context/ProfileContext';
import type { ScanAction } from '@/hooks/useScanReducer';

interface UseCommitFlowParams {
  draft: WineDraft | null;
  rawFile: File | null;
  previewUrl: string | null;
  inventory: Wine[];
  duplicateCandidate: DuplicateCandidate | null;
  setDuplicateCandidate: (candidate: DuplicateCandidate | null) => void;
  isPremium: boolean;
  profileCurrency: Currency;
  session: ScanSession;
  dispatch: React.Dispatch<ScanAction>;
  onClose: () => void;
  onWineCommitted?: (docId: string | string[]) => void;
  onCameraCapture: (file: File) => void;
  setUpgradePromptOpen: (open: boolean) => void;
}

interface UseCommitFlowReturn {
  commitStage: CommitStage;
  commitError: string | null;
  lastCommittedName: React.MutableRefObject<string>;
  handleConfirm: () => Promise<void>;
  handleCommitAnimationComplete: () => void;
  handleScanAnother: () => void;
  handleDone: () => void;
  /** Returns to the draft with the user's edits intact, clearing the failure. */
  handleDismissCommitError: () => void;
}

export function useCommitFlow({
  draft,
  rawFile,
  previewUrl,
  inventory,
  duplicateCandidate,
  setDuplicateCandidate,
  isPremium,
  profileCurrency,
  session,
  dispatch,
  onClose,
  onWineCommitted,
  onCameraCapture,
  setUpgradePromptOpen,
}: UseCommitFlowParams): UseCommitFlowReturn {
  const [commitStage, setCommitStage] = useState<CommitStage>('idle');
  const [commitError, setCommitError] = useState<string | null>(null);
  const lastCommittedDocId = useRef<string | null>(null);
  const lastCommittedName = useRef<string>('');

  const handleConfirm = useCallback(async () => {
    if (!draft) return;
    const draftFields = draft.fields;

    // 0a. Bottle cap check
    const addingQuantity = draftFields.quantity || 1;
    const currentTotal = inventory.reduce((sum, w) => sum + (Number(w.quantity) || 0), 0);
    if (!isPremium && currentTotal + addingQuantity > CONFIG.FREE_TIER.MAX_BOTTLES) {
      setUpgradePromptOpen(true);
      return;
    }

    // 0. Production write guard
    const confirmed = await confirmProdWrite();
    if (!confirmed) return;

    // 1. Check for duplicates
    const dupes = findDuplicates(draftFields, inventory);
    if (dupes.length > 0 && !duplicateCandidate) {
      setDuplicateCandidate(dupes[0]);
      return;
    }

    // 2. Proceed with commit — show saving animation
    dispatch({ type: 'START_COMMIT' });
    setCommitError(null);
    setCommitStage('saving');

    try {
      const cleanedFields = sanitizeWineName(draftFields);
      const wineData = {
        producer: cleanedFields.producer || '',
        name: cleanedFields.name || '',
        vintage: cleanedFields.vintage || 0,
        type: cleanedFields.type || 'Red',
        grapeVarieties: cleanedFields.grapeVarieties ?? [],
        region: cleanedFields.region || '',
        country: cleanedFields.country || '',
        quantity: cleanedFields.quantity || 1,
        drinkFrom: cleanedFields.drinkFrom || 0,
        drinkUntil: cleanedFields.drinkUntil || 0,
        maturity: cleanedFields.maturity || 'Unknown',
        tastingNotes: cleanedFields.tastingNotes || '',
        price: cleanedFields.price || 0,
        priceCurrency: cleanedFields.priceCurrency || profileCurrency,
        format: cleanedFields.format || '750ml',
        appellation: cleanedFields.appellation || '',
        personalNote: cleanedFields.personalNote || '',
      } as Omit<Wine, 'id'>;

      const docId = await inventoryService.addWine(wineData);
      if (!docId) throw new Error('Failed to save to cellar');

      // 3. Upload image + thumbnail — awaited so the URL is saved before success
      if (rawFile) {
        try {
          const [fullBlob, thumbBlob] = await Promise.all([
            compressImageForStorage(rawFile),
            compressImageForThumbnail(rawFile),
          ]);
          const { imageUrl, thumbnailUrl } = await uploadLabelImage(fullBlob, thumbBlob, docId);
          await inventoryService.updateFields(docId, { imageUrl, thumbnailUrl });
        } catch (err) {
          captureError(err, { context: 'scan_register_image' });
        }
      }

      // Fire-and-forget enrichment (tasting notes, drink window, cepage, rating)
      enrichWine(docId, wineData as Partial<Wine>).catch(err =>
        captureError(err, { context: 'scan_register' }));

      lastCommittedDocId.current = docId;
      lastCommittedName.current = `${draftFields.vintage || ''} ${draftFields.producer || 'Wine'}`.trim();

      if (session.isActive) {
        session.commitInSession(docId, draftFields.producer || '', draftFields.vintage || 0);
      }

      dispatch({ type: 'COMMIT_SUCCESS' });
      trackEvent('wine_committed');
      setCommitStage('success');

      // 4. Show toast with undo (10s window)
      showToast({
        tone: 'success',
        message: `${lastCommittedName.current} added to cellar`,
        actionLabel: 'UNDO',
        duration: 10000,
        onAction: async () => {
          hapticHeavy();
          await inventoryService.deleteWine(docId);
          deleteLabelImage(docId).catch(() => {});
          showToast({ tone: 'neutral', message: 'Removed.' });
        },
      });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to save';
      captureError(e, { context: 'scan_commit' });
      dispatch({ type: 'COMMIT_FAIL', error: message });
      setCommitError(message);
      setCommitStage('error');
    }
  }, [draft, rawFile, inventory, duplicateCandidate, isPremium, profileCurrency, session]);

  // Back to the draft, edits intact. Without this the only states reachable
  // from a failed commit were "retry" and "lose the work".
  const handleDismissCommitError = useCallback(() => {
    setCommitError(null);
    setCommitStage('idle');
  }, []);

  const handleCommitAnimationComplete = useCallback(() => {
    setCommitStage('idle');
    dispatch({ type: 'SHOW_SUCCESS_SCREEN' });
  }, [dispatch]);

  const handleScanAnother = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    if (!session.isActive) session.startSession();
    openFilePicker({ capture: true }, onCameraCapture);
    dispatch({ type: 'SCAN_NEXT' });
    setCommitStage('idle');
  }, [previewUrl, session, onCameraCapture, dispatch]);

  const handleDone = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    if (session.isActive) {
      const bottles = session.endSession();
      const allDocIds = bottles.map(b => b.docId);
      if (lastCommittedDocId.current && !allDocIds.includes(lastCommittedDocId.current)) {
        allDocIds.push(lastCommittedDocId.current);
      }
      onClose();
      if (allDocIds.length > 0) onWineCommitted?.(allDocIds);
    } else {
      const docId = lastCommittedDocId.current;
      onClose();
      if (docId) onWineCommitted?.(docId);
    }
  }, [previewUrl, onClose, onWineCommitted, session]);

  return {
    commitStage,
    commitError,
    lastCommittedName,
    handleConfirm,
    handleCommitAnimationComplete,
    handleScanAnother,
    handleDone,
    handleDismissCommitError,
  };
}
